import {
  Accordion,
  AccordionSummary,
  Avatar,
  Typography,
  AccordionDetails,
  Stack,
  Box,
  useTheme,
  useMediaQuery,
  Link,
} from '@mui/material';
import { IBurgerMenuItem } from 'API/Home/burgerMenuItems';
import { getSupplierRedirectCode } from 'API/Treatment/treatment';
import { LawyerDialog } from 'Components/LayerDialog';
import { eshetTursRedirectCode, flyingCarpetRedirectCode } from 'Config';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useImposter } from 'Hooks';
import { useIsLawyer } from 'Hooks/useIsLawyer';
import { AccordionArrowWhite2 } from 'Images';
import { getIsLawyerAndUser } from 'Utils/lawyers';
import React, { useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface IBurgerMenuAccordion {
  accordionIndex: number;
  burgerItem: IBurgerMenuItem;
  isExpanded: boolean;
  setExpandedAccordionIndex: Function;
  closeMenu?: Function;
}

export const BurgerMenuAccordion = ({
  accordionIndex,
  burgerItem,
  isExpanded,
  setExpandedAccordionIndex,
  closeMenu,
}: IBurgerMenuAccordion) => {
  const [showDialog, setShowDialog] = useState(false);

  const navigate = useNavigate();

  const { pathname } = useLocation();

  const accordionRef = useRef<HTMLDivElement | null>(null);

  const firstSubCategoryItemRef = useRef<HTMLAnchorElement | null>(null);
  const lastSubCategoryItemRef = useRef<HTMLAnchorElement | null>(null);

  const theme = useTheme();
  const isScreenXl = useMediaQuery(theme.breakpoints.up('xl'));

  const dictionary = useDictionary(Dictionary, DICTIONARY.General);

  const imposter = useImposter();

  const isLawyer = useIsLawyer();

  const itemNavigationHandler = async (item: IBurgerMenuItem) => {
    if (
      (getIsLawyerAndUser()
        ? sessionStorage.getItem('isLawyer') === 'true'
        : isLawyer) &&
      !imposter.imposterId
    ) {
      setShowDialog(true);
      return;
    }
    if (item.internalLink) {
      navigate(`/${item.internalLink}`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (!isScreenXl && closeMenu) closeMenu();
    } else if (item.externalLink) {
      let code = 0;
      let urlParams = '';
      if (item.externalLink === dictionary.eshetTursUrl) {
        code = eshetTursRedirectCode;
        urlParams = '/?segmentId=18&subSegmentId=1&auth=';
      } else if (item.externalLink === dictionary.flyingCarpetUrl) {
        code = flyingCarpetRedirectCode;
        urlParams = '/?code=';
      }

      if (code !== 0) {
        const redirectCode = await getSupplierRedirectCode(code);
        if (redirectCode)
          window.open(
            `${item.externalLink}${urlParams}${encodeURIComponent(
              redirectCode
            )}`
          );
      } else window.open(item.externalLink);
    }
  };

  const keyboardMenuAccessibility = (event: React.KeyboardEvent) => {
    event.stopPropagation();

    if (accordionRef.current) {
      if (event.key === 'Escape') {
        accordionRef.current.click();
        accordionRef.current.focus();
      } else if (event.key === 'Enter') (event.target as HTMLElement).click();
    }

    if (firstSubCategoryItemRef.current && lastSubCategoryItemRef.current) {
      if (
        event.key === 'Tab' &&
        event.currentTarget === lastSubCategoryItemRef.current
      ) {
        event.preventDefault();
        firstSubCategoryItemRef.current.focus();
      }
      if (
        event.key === 'Tab' &&
        event.shiftKey &&
        event.currentTarget === firstSubCategoryItemRef.current
      ) {
        event.preventDefault();
        lastSubCategoryItemRef.current.focus();
      }
    }
  };

  const memoizedBurgerMenuSubItems = useMemo(() => {
    return burgerItem.subItems?.map(
      (subItem: IBurgerMenuItem, index: number) => {
        return (
          <Link
            key={index}
            variant={'body6'}
            color={'#fff'}
            ref={
              index === 0
                ? firstSubCategoryItemRef
                : index === burgerItem.subItems!.length - 1
                ? lastSubCategoryItemRef
                : undefined
            }
            sx={{
              padding: '20px 0 20px 60px',
              backgroundColor:
                subItem.internalLink && pathname === `/${subItem.internalLink}`
                  ? '#071b4a'
                  : undefined,
              transition: '.5s',
              textDecoration: 'none',
              '&:hover': {
                cursor: 'pointer',
                bgcolor: '#071b4a',
                transition: '.5s',
              },
            }}
            onClick={(event: React.MouseEvent) => {
              event.stopPropagation();
              itemNavigationHandler(subItem);
            }}
            onKeyDown={keyboardMenuAccessibility}
            tabIndex={0}
          >
            {subItem.description}
            {subItem.isNewItem && (
              <Box
                sx={{
                  display: 'inline-block',
                  padding: '0 8px',
                  ml: '10px',
                  fontWeight: 700,
                  fontSize: '16px',
                  bgcolor: 'purple2.main',
                  borderRadius: '0 12px 0 6px',
                }}
              >
                {dictionary.sidebar.new}
              </Box>
            )}
          </Link>
        );
      }
    );
  }, [burgerItem.subItems, itemNavigationHandler, pathname, dictionary]);

  const accessibilityKeyboardHandler = (event: React.KeyboardEvent) => {
    event.stopPropagation();

    if ((event.key === 'Enter' || event.key === ' ') && !isExpanded) {
      (event.target as HTMLElement).click();
      firstSubCategoryItemRef.current?.focus();
    } else if (event.key === 'Escape' && isExpanded)
      (event.target as HTMLElement).click();
    return;
  };

  return (
    <Box component={'li'}>
      <Accordion
        TransitionProps={{ unmountOnExit: true }}
        disableGutters
        expanded={isExpanded}
        ref={accordionRef}
        sx={{
          backgroundColor: 'transparent',
          boxShadow: 'none',
          transition: '.3s',
          '&:before': { display: 'none' },
          '&:hover': {
            cursor: 'pointer',
            bgcolor: 'primary.shade_500',
            transition: '.3s',
          },
        }}
        onClick={() =>
          setExpandedAccordionIndex(isExpanded ? -1 : accordionIndex)
        }
        onKeyDown={accessibilityKeyboardHandler}
      >
        <AccordionSummary
          expandIcon={
            burgerItem.subItems ? (
              <Avatar
                alt={''}
                src={AccordionArrowWhite2}
                sx={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  bgcolor: isExpanded ? 'purple2.main' : undefined,
                  transition: '.3s',
                }}
              />
            ) : null
          }
          aria-controls='panel1a-content'
          id='panel1a-header'
          sx={{
            backgroundColor: isExpanded ? 'primary.shade_500' : 'transparent',
            padding: '10px 20px 10px 20px',
          }}
          onClick={() => itemNavigationHandler(burgerItem)}
        >
          <Stack direction={'row'} gap={'16px'}>
            <Avatar
              alt=''
              src={burgerItem.iconUrl}
              sx={{ height: '24px', width: '24px' }}
            />
            <Typography variant={'body6'} color={'#fff'}>
              {burgerItem.description}
            </Typography>
          </Stack>
        </AccordionSummary>
        {burgerItem.subItems ? (
          <AccordionDetails sx={{ bgcolor: 'primary.shade_600', padding: 0 }}>
            <Stack role='listitem'>{memoizedBurgerMenuSubItems}</Stack>
          </AccordionDetails>
        ) : null}
      </Accordion>
      {isLawyer && (
        <LawyerDialog showDialog={showDialog} setShowDialog={setShowDialog} />
      )}
    </Box>
  );
};
